import { Controller } from "@hotwired/stimulus"

/**
 * Collapsible sidebar section for the Support tickets sidebar.
 *
 * Each section (Inboxes, Saved Views, Type, Sentiment, Status, Waiting Reason, ITSM)
 * can be collapsed/expanded individually. State persists in:
 *  - localStorage (per-section key, source of truth on the client)
 *  - cookie `support_sidebar_collapsed` (comma-separated list, read SSR on next render)
 *
 * The cookie is what makes the initial render match the user's last preference,
 * eliminating the expand-then-collapse flash on page load.
 *
 * Usage:
 *   <div data-controller="support--sidebar-section"
 *        data-support--sidebar-section-key-value="inboxes">
 *     <button data-action="click->support--sidebar-section#toggle"
 *             data-support--sidebar-section-target="trigger"
 *             aria-expanded="true">
 *       <span>Inboxes</span>
 *       <svg data-support--sidebar-section-target="icon">...</svg>
 *     </button>
 *     <div data-support--sidebar-section-target="content">...</div>
 *   </div>
 */
export default class extends Controller {
  static targets = ["trigger", "content", "icon"]
  static values = { key: String }

  static STORAGE_PREFIX = "support.sidebar.section."
  static COOKIE_NAME = "support_sidebar_collapsed"

  connect() {
    const stored = this._readStorage()
    if (stored !== null) {
      const serverState =
        this.triggerTarget.getAttribute("aria-expanded") === "true"
      if (serverState !== stored) this._apply(stored)
    }
  }

  toggle(event) {
    event?.preventDefault()
    const next = this.triggerTarget.getAttribute("aria-expanded") !== "true"
    this._apply(next)
    this._writeStorage(next)
    this._writeCookie(next)
  }

  _apply(open) {
    this.triggerTarget.setAttribute("aria-expanded", open ? "true" : "false")
    this.contentTarget.classList.toggle("hidden", !open)
    if (this.hasIconTarget) {
      this.iconTarget.classList.toggle("rotate-0", open)
      this.iconTarget.classList.toggle("-rotate-90", !open)
    }
  }

  _storageKey() {
    return `${this.constructor.STORAGE_PREFIX}${this.keyValue}`
  }

  _readStorage() {
    try {
      const raw = window.localStorage.getItem(this._storageKey())
      if (raw === null) return null
      return raw === "1"
    } catch {
      return null
    }
  }

  _writeStorage(open) {
    try {
      window.localStorage.setItem(this._storageKey(), open ? "1" : "0")
    } catch {
      // Ignore — private mode, quota exceeded, etc.
    }
  }

  _writeCookie(open) {
    const set = this._currentCookieSet()
    if (open) set.delete(this.keyValue)
    else set.add(this.keyValue)
    const value = [...set].join(",")
    // 1 year, root path so any /support/... page reads it.
    const maxAge = 60 * 60 * 24 * 365
    document.cookie = `${this.constructor.COOKIE_NAME}=${encodeURIComponent(value)}; path=/; max-age=${maxAge}; SameSite=Lax`
  }

  _currentCookieSet() {
    const raw = document.cookie
      .split(";")
      .map((c) => c.trim())
      .find((c) => c.startsWith(`${this.constructor.COOKIE_NAME}=`))
    if (!raw) return new Set()
    const value = decodeURIComponent(raw.split("=")[1] || "")
    return new Set(
      value
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),
    )
  }
}
